import { useState } from 'react';
import { SectionHeading } from './ui';

export default function WhySection() {
  return (
    <section>
      <SectionHeading
        eyebrow="Vì sao cần"
        title="Vì sao cần máy tính ký quỹ?"
        subtitle="Ký quỹ là số tiền bị giữ cho mỗi vị thế — nắm rõ nó trước khi vào lệnh là bước bảo vệ vốn đầu tiên."
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Hero banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-800 via-violet-900 to-slate-950 p-7 md:p-9 lg:col-span-2">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 right-1/3 h-56 w-56 rounded-full bg-fuchsia-400/10 blur-3xl" />

          <div className="relative">
            <p className="font-display text-xl font-bold leading-snug text-white md:text-2xl">
              Hầu hết tài khoản bị cháy không phải vì phân tích sai, mà vì{' '}
              <span className="text-fuchsia-300">ký quỹ không đủ</span> vào đúng lúc thị trường biến động.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-violet-100/70">
              Một lệnh mở quá lớn sẽ "đóng băng" gần hết số dư của bạn. Khi giá đi ngược chiều, không còn biên an toàn
              để chịu đựng, sàn sẽ tự động đóng lệnh. Máy tính ký quỹ giúp bạn biết trước con số đó.
            </p>

            <div className="mt-7 grid grid-cols-3 gap-3">
              <BannerStat value="1:500" label="Đòn bẩy phổ biến" />
              <BannerStat value="0,2%" label="Ký quỹ tương ứng" />
              <BannerStat value="100%" label="Kiểm soát vốn" />
            </div>
          </div>
        </div>

        {/* Golden rule */}
        <div className="relative flex flex-col justify-between overflow-hidden rounded-3xl border border-fuchsia-200/70 bg-gradient-to-br from-fuchsia-50 to-white p-7 md:p-9">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-semibold text-fuchsia-700">
              <i className="ri-vip-crown-line" />
              Nguyên tắc vàng
            </span>
            <p className="mt-5 font-display text-5xl font-bold leading-none text-fuchsia-600 md:text-6xl">≤ 30%</p>
            <p className="mt-3 text-sm font-semibold leading-snug text-slate-800">
              Tổng ký quỹ đang bị giữ không nên vượt quá 30% số dư tài khoản.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Chừa lại ít nhất 70% biên an toàn để vị thế có không gian "thở" khi thị trường biến động.
            </p>
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs font-medium text-fuchsia-600">
            <i className="ri-shield-check-line" />
            Giữ biên an toàn = giữ tài khoản
          </div>
        </div>
      </div>

      {/* Bento grid feature section */}
      <div className="mt-14">
        <div className="mb-10 text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-purple-600">LỢI ÍCH CỐT LÕI</p>
          <h3 className="text-3xl font-extrabold text-slate-900 lg:text-4xl">
            Tại sao Trader cần sử dụng Máy tính Ký quỹ?
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* 01 — Hero card */}
          <div className="relative flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all hover:shadow-md md:col-span-2 lg:p-10">
            <div>
              <span className="mb-4 inline-block rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-600">
                01 / AN TOÀN TỐI ĐA
              </span>
              <h4 className="mb-3 text-2xl font-extrabold leading-snug text-slate-900">
                Tránh bị ép đóng lệnh &amp; Bảo vệ tài khoản khỏi rủi ro cháy
              </h4>
              <p className="max-w-xl text-sm leading-relaxed text-slate-600">
                Biết chính xác số tiền bị phong tỏa cho mỗi vị thế giúp bạn không bao giờ rơi vào tình trạng ký quỹ
                không đủ, ngăn chặn triệt để tình trạng sàn tự động cắt lỗ lệnh khi thị trường biến động mạnh ngược
                hướng.
              </p>
            </div>
            <div className="mt-8 flex items-center justify-between gap-4 border-t border-slate-100 pt-6">
              <p className="text-xs font-medium text-slate-400">Giữ biên độ an toàn cho mọi chiến lược giao dịch</p>
              <LeverageConverter />
            </div>
          </div>

          {/* 02 — Phân bổ vốn */}
          <div className="flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all hover:shadow-md">
            <div>
              <span className="mb-4 inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-600">
                02 / PHÂN BỔ VỐN
              </span>
              <h4 className="mb-2 text-xl font-extrabold text-slate-900">Phân bổ vốn hợp lý</h4>
              <p className="text-sm leading-relaxed text-slate-600">
                Tính tổng ký quỹ cho nhiều lệnh cùng lúc để chừa đủ biên an toàn, không dồn quá nhiều vốn vào một vị
                thế duy nhất.
              </p>
            </div>
          </div>

          {/* 03 — Minh bạch */}
          <div className="flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all hover:shadow-md">
            <div>
              <span className="mb-4 inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-600">
                03 / MINH BẠCH
              </span>
              <h4 className="mb-2 text-xl font-extrabold text-slate-900">Hiểu chi phí thực tế</h4>
              <p className="text-sm leading-relaxed text-slate-600">
                Ký quỹ không phải là phí, nhưng là số vốn bị "đóng băng". Nắm rõ nó để quản lý dòng tiền và thanh
                khoản hiệu quả.
              </p>
            </div>
          </div>

          {/* 04 — So sánh đòn bẩy */}
          <div className="flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all hover:shadow-md md:col-span-2 lg:p-10">
            <div>
              <span className="mb-4 inline-block rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-600">
                04 / SO SÁNH ĐÒN BẨY
              </span>
              <h4 className="mb-3 text-2xl font-extrabold leading-snug text-slate-900">
                So sánh đòn bẩy tức thì giữa 1:100 và 1:500
              </h4>
              <p className="max-w-xl text-sm leading-relaxed text-slate-600">
                Thấy ngay cùng một khối lượng lệnh, mức chênh lệch ký quỹ giữa các tỷ lệ đòn bẩy khác nhau là bao nhiêu
                để lựa chọn phương án tối ưu nhất cho số vốn hiện có.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Khi nào cần dùng — article style */}
      <div className="mt-10">
        <h3 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
          Khi nào cần dùng đến?
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
          Máy tính ký quỹ không chỉ dành cho lần mở lệnh đầu tiên. Đây nên là một bước kiểm tra thường xuyên trong quy
          trình giao dịch của bạn — từ người mới bắt đầu cho đến trader có nhiều năm kinh nghiệm.
        </p>

        <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">
          Thời điểm quan trọng nhất là <strong className="font-semibold text-slate-900">trước mỗi lệnh giao dịch</strong>.
          Trước khi bấm nút vào lệnh, hãy nhập sản phẩm, khối lượng và đòn bẩy để biết chính xác số tiền sẽ bị
          "đóng băng". Nếu bạn <strong className="font-semibold text-slate-900">mở nhiều vị thế cùng lúc</strong>, hãy
          cộng dồn toàn bộ ký quỹ để chắc chắn tổng số tiền bị giữ không vượt quá ngưỡng an toàn. Khi{' '}
          <strong className="font-semibold text-slate-900">số dư thay đổi</strong> (nạp thêm hoặc rút bớt tiền), hoặc khi
          bạn <strong className="font-semibold text-slate-900">đổi đòn bẩy hay chuyển sang sản phẩm khác</strong> như
          vàng, chỉ số — hãy tính lại, vì ký quỹ yêu cầu sẽ thay đổi theo từng trường hợp.
        </p>

        <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-fuchsia-600">Người mới bắt đầu</h4>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Hãy dùng công cụ này như một <strong className="font-semibold text-slate-900">checklist bắt buộc</strong>{' '}
              trước mỗi lệnh. Tập thói quen ghi lại số ký quỹ và đối chiếu với số dư, tránh để một lệnh duy nhất "nuốt"
              gần hết tài khoản.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-fuchsia-600">Trader có kinh nghiệm</h4>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Tận dụng để <strong className="font-semibold text-slate-900">so sánh đòn bẩy và tái cân bằng nhiều vị
              thế</strong>. Theo dõi mức ký quỹ (margin level) theo thời gian thực để biết khi nào nên giảm khối lượng
              hoặc nạp thêm vốn trước khi chạm ngưỡng Stop Out.
            </p>
          </div>
        </div>

        <div className="mt-7">
          <h4 className="text-sm font-bold uppercase tracking-wide text-fuchsia-600">Ví dụ cụ thể</h4>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Bạn có tài khoản <span className="font-mono font-semibold text-slate-900">1.000 USD</span> và muốn giao
            dịch <span className="font-mono font-semibold text-slate-900">1 Lot EUR/USD</span> ở giá{' '}
            <span className="font-mono font-semibold text-slate-900">1,0845</span> với đòn bẩy{' '}
            <span className="font-mono font-semibold text-slate-900">1:500</span>. Ký quỹ yêu cầu khoảng{' '}
            <span className="font-mono font-semibold text-fuchsia-600">216,90 USD</span> — tức khoảng{' '}
            <strong className="font-semibold text-fuchsia-600">21,7% số dư</strong>, vẫn nằm trong ngưỡng an toàn 30%.
            Nhưng nếu mở thêm 1 Lot nữa, tổng ký quỹ lên <span className="font-mono font-semibold text-slate-900">433,80
            USD</span> (43,4%), đã vượt ngưỡng khuyến nghị — lúc này nên giảm khối lượng hoặc nạp thêm vốn.
          </p>
        </div>

        <div className="mt-7 border-l-2 border-fuchsia-300 pl-4">
          <h4 className="text-sm font-bold uppercase tracking-wide text-fuchsia-600">Lưu ý rủi ro</h4>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Ký quỹ thấp <strong className="font-semibold text-slate-900">không đồng nghĩa với an toàn</strong>. Đòn bẩy
            cao giúp bạn mở lệnh lớn với ít vốn, nhưng cũng khuếch đại mức lỗ khi giá đi ngược hướng. Luôn đặt Stop Loss
            và không để tổng ký quỹ vượt quá <strong className="font-semibold text-fuchsia-600">30% số dư</strong>.
          </p>
        </div>
      </div>

      {/* Integration steps — card-free process flow */}
      <div className="py-20">
        <div className="mb-16 text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-purple-600">CHIẾN LƯỢC GIAO DỊCH</p>
          <h3 className="mb-4 text-center text-3xl font-extrabold text-slate-900 lg:text-4xl">
            Cách tích hợp Máy tính Ký quỹ vào chiến lược
          </h3>
          <p className="mx-auto max-w-xl text-center text-sm leading-relaxed text-slate-500">
            Biết ký quỹ chỉ là bước đầu — đây là cách biến nó thành thói quen bảo vệ vốn chuyên nghiệp.
          </p>
        </div>

        <div className="relative grid grid-cols-1 gap-12 md:grid-cols-4 md:gap-8">
          {[
            {
              step: '01',
              icon: 'ri-file-text-line',
              title: 'Kiểm tra trước mỗi lệnh',
              desc: 'Mở máy tính ký quỹ, nhập sản phẩm, khối lượng và đòn bẩy. Xác nhận số tiền cần giữ trước khi bấm vào lệnh.',
            },
            {
              step: '02',
              icon: 'ri-scales-line',
              title: 'Đối chiếu với số dư',
              desc: 'So sánh ký quỹ yêu cầu với số dư tài khoản. Đảm bảo tổng ký quỹ không vượt quá 30% số dư hiện có.',
            },
            {
              step: '03',
              icon: 'ri-shield-check-line',
              title: 'Thiết lập Stop Loss',
              desc: 'Tính ký quỹ giúp biết mức đòn bẩy. Đặt Stop Loss sao cho khoản lỗ tối đa không quá 2% số dư tài khoản.',
            },
            {
              step: '04',
              icon: 'ri-refresh-line',
              title: 'Theo dõi liên tục',
              desc: 'Khi giá thay đổi, ký quỹ cũng thay đổi. Kiểm tra lại nếu thị trường biến động mạnh hoặc mở thêm vị thế.',
            },
          ].map((item) => (
            <div key={item.step} className="relative flex flex-col items-start">
              <div className="mb-6 flex w-full items-center justify-between">
                <span className="text-4xl font-black tracking-tighter text-purple-600">{item.step}</span>
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-50 text-purple-600">
                  <i className={`${item.icon} text-lg`} />
                </span>
              </div>
              <p className="mb-3 text-xl font-extrabold leading-snug text-slate-900">{item.title}</p>
              <p className="text-sm leading-relaxed text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BannerStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <p className="font-mono text-xl font-bold tabular-nums text-fuchsia-300 md:text-2xl">{value}</p>
      <p className="mt-1 text-[11px] font-medium leading-snug text-violet-100/60">{label}</p>
    </div>
  );
}

const LEVERAGE_OPTIONS = [100, 200, 500];

function LeverageConverter() {
  const [leverage, setLeverage] = useState(500);
  const marginPercent = (100 / leverage).toFixed(2).replace('.', ',');

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5 rounded-full bg-slate-100 p-1">
        {LEVERAGE_OPTIONS.map((option) => {
          const active = option === leverage;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setLeverage(option)}
              className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${
                active
                  ? 'bg-purple-600 text-white'
                  : 'text-slate-500 hover:text-purple-600'
              }`}
            >
              1:{option}
            </button>
          );
        })}
      </div>
      <span className="whitespace-nowrap rounded-lg bg-purple-50 px-2.5 py-1.5 text-xs font-bold text-purple-600">
        1:{leverage} → {marginPercent}%
      </span>
    </div>
  );
}